/**
 * DesktopMasthead
 *
 * Full-width editorial masthead for the desktop weekly planner.
 * Shows "This Week's Menu" with seasonal subtitle and week navigation.
 */

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useRouter } from 'expo-router';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaShadows } from '../../styles/theme';
import { useTranslation } from 'react-i18next';

interface DesktopMastheadProps {
  weekStartDate: string;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
}

const SEASON_KEYS: Record<number, string> = {
  0: 'winter', 1: 'winter', 2: 'spring',
  3: 'spring', 4: 'spring', 5: 'summer',
  6: 'summer', 7: 'summer', 8: 'fall',
  9: 'fall', 10: 'fall', 11: 'winter',
};

export const DesktopMasthead: React.FC<DesktopMastheadProps> = ({
  weekStartDate,
  onPreviousWeek,
  onNextWeek,
}) => {
  const { t, i18n } = useTranslation();
  const router = useRouter();

  const { weekRange, seasonYear } = useMemo(() => {
    const locale = i18n.language === 'fr' ? 'fr-CA' : 'en-US';
    const start = new Date(weekStartDate + 'T12:00:00');
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const startMonth = start.toLocaleDateString(locale, { month: 'short' });
    const endMonth = end.toLocaleDateString(locale, { month: 'short' });
    const range = startMonth === endMonth
      ? `${startMonth} ${start.getDate()} – ${end.getDate()}`
      : `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}`;

    const seasonKey = SEASON_KEYS[start.getMonth()];
    const year = start.getFullYear();

    return { weekRange: range, seasonYear: `${t(`thisWeek.seasons.${seasonKey}`)} ${year}` };
  }, [weekStartDate, i18n.language, t]);

  return (
    <View style={styles.container}>
      <Text style={styles.season}>{seasonYear}</Text>
      <View style={styles.titleRow}>
        <TouchableOpacity onPress={onPreviousWeek} style={styles.navButton}>
          <Feather name="chevron-left" size={24} color={Paprika.textMuted} />
        </TouchableOpacity>
        <Text style={styles.title}>{t('thisWeek.weeklyMenu')}</Text>
        <TouchableOpacity onPress={onNextWeek} style={styles.navButton}>
          <Feather name="chevron-right" size={24} color={Paprika.textMuted} />
        </TouchableOpacity>
      </View>
      <Text style={styles.dateRange}>{weekRange}</Text>
      <TouchableOpacity
        style={styles.tasteProfileLink}
        onPress={() => router.push('/(app)/taste-profile')}
        activeOpacity={0.7}
      >
        <MaterialCommunityIcons name="creation" size={16} color={Paprika.accent} />
        <Text style={styles.tasteProfileText}>{t('thisWeek.tasteProfile')}</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Paprika.surfaceLowest,
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.lg,
    ...PaprikaShadows.editorial,
  },
  season: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 2,
    marginBottom: 2,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  navButton: {
    padding: PaprikaSpacing.xs,
  },
  title: {
    ...PaprikaTypography.displayMedium,
    color: Paprika.textPrimary,
  },
  dateRange: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginTop: 2,
  },
  tasteProfileLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: PaprikaSpacing.sm,
    padding: PaprikaSpacing.xs,
  },
  tasteProfileText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
  },
});

export default DesktopMasthead;
